import { createElement, lazy, Suspense } from 'react';
import { LeadApproveSendPlaceholder } from '../features/aiDrafts/LeadApproveSendPlaceholder';
import type { LeadPanelComposition, LeadPanelProps } from '../lib/leadPanels';
import { reportError } from './telemetry';

/**
 * Which feature's panel fills which place in the lead view (F11-f spec FR-S1, FR-S2; WI-1.4 plan
 * Decision 2). The composition lives here, in the composition root, and `router.tsx` hands it to the
 * Leads route as a prop, so `src/features/leads` never imports another feature
 * (`features-do-not-import-each-other`) and no feature imports `src/app` (`only-main-imports-app`).
 *
 * **How an owning feature adds its panel**, in its own PR: build the panel in its own folder as a
 * component taking `LeadPanelProps` (`src/lib/leadPanels.ts`), test it there, and add one entry here
 * under its place name, **always as a `lazy()` import** —
 *
 *     sharing: lazy(() => import('../features/sharing/LeadSharingPanel')),
 *
 * so each panel stays in its own chunk and the Leads route's bundle budget does not grow with it
 * (`pnpm check:bundle`). The places are `sharing`, `summary`, `score`, `research`, `draft`, `compose`,
 * `participants`, each one component, and `actions`, an ordered list (F11-h spec FR-V3) — a feature adds
 * its action to the list; an unfilled place renders nothing. F5-g fills `sharing` with
 * `LeadSharingPanel` (F5-g FR-P1), through an exported loader its test resolves; F11-d fills `summary`
 * with the lead triage feature's `LeadTriagePanel` (F11-d spec FR-V1, NFR-7 — this file is the only one
 * that ships and imports `src/features/leadTriage`); F11-c fills `research` with the lead research feature's
 * `LeadResearchPanel` (F11-c spec FR-V1, NFR-7 — likewise the only shipping importer of `src/features/leadResearch`);
 * F20-c fills `compose` with the email feature's
 * `LeadEmailPanel` (F20-c FR-E3) the same way; and F11-h fills `draft` with its *Follow-up draft* panel
 * (F11-h FR-V1) and puts *Approve & send* first in `actions` (F11-h FR-V2, F11-f FR-S1).
 *
 * **While *Approve & send*'s chunk loads, its slot holds the button's room** with a hidden, inert stand-in
 * (`LeadApproveSendPlaceholder`, BUG-265), drawn only where the button will show. With nothing there, the button
 * arrived after the heading row had settled and wrapped *Edit* and *Archive* onto the next line.
 *
 * A panel's render error stays inside its place and is reported to the telemetry seam, never to the
 * console.
 */
/** The sharing panel's chunk, as a default export for `lazy()` — exported for its test (F5-g FR-P1). */
export const loadLeadSharingPanel = () =>
    import('../features/sharing/LeadSharingPanel').then((module) => ({
        default: module.LeadSharingPanel,
    }));

/** The lead's *AI summary* panel chunk (F11-d spec FR-V1; F11-d WI-1.2 plan Decision 1) — exported for its test. */
export const loadLeadTriagePanel = () =>
    import('../features/leadTriage/LeadTriagePanel').then((module) => ({
        default: module.LeadTriagePanel,
    }));

/** The lead's *Research* panel chunk (F11-c spec FR-V1; F11-c WI-1.4 plan Decision 1) — exported for its test. */
export const loadLeadResearchPanel = () =>
    import('../features/leadResearch/LeadResearchPanel').then((module) => ({
        default: module.LeadResearchPanel,
    }));

/** The lead's *Email* panel chunk (F20-c FR-E3) — exported for its test. */
export const loadLeadEmailPanel = () =>
    import('../features/email/recordEntries/LeadEmailPanel').then((module) => ({
        default: module.LeadEmailPanel,
    }));

/** The lead's *Follow-up draft* panel chunk (F11-h FR-V1) — exported for its test. */
export const loadLeadFollowUpDraftPanel = () =>
    import('../features/aiDrafts/LeadFollowUpDraftPanel').then((module) => ({
        default: module.LeadFollowUpDraftPanel,
    }));

/** *Approve & send* / *Send follow-up* (F11-h FR-V2) — exported for its test. */
export const loadLeadApproveSendAction = () =>
    import('../features/aiDrafts/LeadApproveSendAction').then((module) => ({
        default: module.LeadApproveSendAction,
    }));

const LazyLeadApproveSendAction = lazy(loadLeadApproveSendAction);

/**
 * *Approve & send* once its chunk has loaded, its stand-in until then (BUG-265). Written without JSX because this
 * file keeps its `.ts` name, which `e2e/fixtures/routes.setup.ts` reads to load every lazy panel module.
 */
function LeadApproveSendPlace(props: LeadPanelProps) {
    return createElement(
        Suspense,
        { fallback: createElement(LeadApproveSendPlaceholder, props) },
        createElement(LazyLeadApproveSendAction, props),
    );
}

export const leadPanelComposition: LeadPanelComposition = {
    panels: {
        sharing: lazy(loadLeadSharingPanel),
        summary: lazy(loadLeadTriagePanel),
        research: lazy(loadLeadResearchPanel),
        draft: lazy(loadLeadFollowUpDraftPanel),
        compose: lazy(loadLeadEmailPanel),
        // In order: *Approve & send* first (F11-f FR-S1).
        actions: [LeadApproveSendPlace],
    },
    reportPanelError: (error, place) => reportError({ error, where: `lead panel ${place}` }),
};
